import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

// components
import CartUpsellModalBase, { modalStyles } from '../CartUpsellModalBase';
import ModalBase from '@molecules/ModalBase';

// functionality
import { LoadingContext } from '@context/LoadingProvider';
import { addEventRegistrationToCart } from '@general/helpers/cart.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import useMembersByCompany from '@hooks/apiLegacy/useMembersByCompany';
import useMembersIneligibleForEventRegistration from '@hooks/useMembersIneligibleForEventRegistration';
import useViewer from '@hooks/useViewer';
import useViewerCompanyIdEffective from '@hooks/useViewerCompanyIdEffective';

const CartUpsellEventModal = ({ eventCode, isOpen, onClose }) => {
    // ------------------------------
    // State
    // ------------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const { membersIneligibleForEventRegistration } = useMembersIneligibleForEventRegistration({ eventCode });

    // ------------------------------
    // Hooks
    // ------------------------------

    const history = useHistory();
    const { viewer } = useViewer();
    const { viewerCompanyId } = useViewerCompanyIdEffective();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { coworkers } = useMembersByCompany(viewerCompanyId);
    const { event: eventFromAPI } = useEventFromAPI({ code: eventCode });

    // ------------------------------
    // Functions
    // ------------------------------

    const registerMembers = async ({ selectedMembers, shouldAddNewMember }) => {
        onClose();

        if (selectedMembers.length > 0) {
            setIsLoading(true);

            // Need to add event registrations sequentially for stripe payment intent to update correctly
            await selectedMembers.reduce(async (previousPromise, member) => {
                await previousPromise;
                return addEventRegistrationToCart({
                    cart,
                    event: eventFromAPI,
                    memberId: member.id,
                    viewerId: viewer.recordId,
                });
            }, Promise.resolve());

            setIsLoading(false);
        }

        await refetch();

        if (shouldAddNewMember) {
            history.push(`/event-registration/${eventCode}/info/new`);
        }
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!eventFromAPI || !coworkers || !membersIneligibleForEventRegistration) {
        return null;
    }

    return (
        <ModalBase
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            stylesBody={modalStyles.body}
            stylesContent={modalStyles.content}
            stylesOverlay={modalStyles.overlay}
            onClose={onClose}
        >
            <CartUpsellModalBase
                cart={cart}
                coworkers={coworkers}
                heading={`Who do you want to register for ${eventFromAPI.title}?`}
                membersToExclude={membersIneligibleForEventRegistration}
                viewerId={viewer.recordId}
                onAddToCart={registerMembers}
                onCancelClick={onClose}
            />
        </ModalBase>
    );
};

CartUpsellEventModal.propTypes = {
    eventCode: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default CartUpsellEventModal;
